import React, { Fragment } from "react";
import { WidgetProps } from '@rjsf/core';
import { CheckboxProps, Form, Header, Radio } from "semantic-ui-react";
import DateTimePicker from "../components/DateTimePicker/DateTimePicker";
import { ErrorList } from '../components/ErrorList';
import { useSchemaTranslation } from "../../../useSchemaTranslation";
import { roundToNearestHour } from "../components/DateTimePicker/DateTimePicker.utils";
import { getDefaultDateRangeValues } from "../components/DateTimePicker/getDefaultDateRangeValues";
import { STRICT_TIME_REGEX } from "../../../constants/formats";

export type TValue = {
  start?: string,
  end?: string;
  startsOnSubmission?: "Y" | "N" | string;

  untouchedStart?: boolean;
  untouchedEnd?: boolean;
};

export interface IDateRangeWidgetOptions {
  minDate?: string | true;
  maxDate?: string | true;

  hourRoundingValue?: number;
  pastDateWarning?: boolean;


  start?: {
    defaultTime?: string
    hourRoundingValue?: number;
  },

  end?: {
    defaultTime?: string,
    hourRoundingValue?: number;
  }

}


const DateRangeWidget: React.FC<WidgetProps & { options: IDateRangeWidgetOptions; errorSchema?: any }> = ({
  id,
  disabled,
  value: uknownValue,
  onChange: uknownOnChange,
  onBlur,
  onFocus,
  options,
  autofocus,
  label,
  schema,
  readonly,
  errorSchema,
  rawErrors,
  ...inputProps
}) => {
  const t = useSchemaTranslation().t

  const value: TValue | undefined = uknownValue;
  const onChange = (value?: TValue) => { uknownOnChange(value); };

  const schemaFormat = (schema.format as "date" | "time" | "date-time") || "date-time";

  const {
    minDate,
    maxDate,

    hourRoundingValue,

    start: startsOptions,
    end: endsOptions,

    pastDateWarning = false
  } = options

  const update = (updates: Partial<TValue>) => {
    onChange({ ...(value || {}), ...updates, })
  };



  const getValue = (key: keyof TValue) => value && value[key] ? value[key] : undefined;
  const getMaxMinOptions = (opt: string | true | undefined) => opt === true ? new Date().toISOString() : opt

  const untouchedStart = getValue("untouchedStart") as boolean | undefined;
  const startsOnSubmission = getValue("startsOnSubmission");
  const starts = getValue("start") as string | undefined;
  const ends = getValue("end") as string | undefined;;

  const defaultRangeOptions = {
    start: {
      defaultTime: startsOptions?.defaultTime,
      hourRoundingValue: startsOptions?.hourRoundingValue || hourRoundingValue
    },
    end: {
      defaultTime: endsOptions?.defaultTime,
      hourRoundingValue: endsOptions?.hourRoundingValue || hourRoundingValue
    }
  };

  const updateStarts = (newValue?: string) => {
    if (newValue !== value?.start) {
      const nextValue = {
        ...(value || {}),
        "untouchedStart": false,
        "start": newValue, startsOnSubmission: "N"
      };
      let options = { ...defaultRangeOptions };
      let defaultRange;
      const noStartDate = newValue && RegExp(STRICT_TIME_REGEX).test(newValue)

      if (noStartDate) {
        options.start.defaultTime = newValue;
        defaultRange = getDefaultDateRangeValues(nextValue, options);
        nextValue.start = defaultRange.start;
      } else {
        defaultRange = getDefaultDateRangeValues(nextValue, options);
      };

      nextValue.end = nextValue?.end || (nextValue.start ? defaultRange?.end : undefined)

      update(nextValue);
    }
  };

  const updateEnds = (newValue?: string) => {
    update({ untouchedEnd: false, "end": newValue });
  };

  const updateStartsOnSubmission: CheckboxProps["onChange"] = (_1, { value: startsOnSubmission }) => {
    if (value?.untouchedStart) {
      var confirm = window.confirm("Oletko varma että haluat muuttaa myönnetyn luvan alkamisaikaa");
      if (!confirm) {
        return
      }
    };

    if (startsOnSubmission === "Y") {
      const nextValue = { ...(value || {}) };
      let options = { ...defaultRangeOptions };
      options.start.defaultTime = undefined;
      nextValue.start = getDefaultDateRangeValues(nextValue, options).start;
      const defaultRange = getDefaultDateRangeValues(nextValue, options);

      update({
        ...nextValue,
        "untouchedStart": false,
        "startsOnSubmission": "Y",
        "end": nextValue?.end || (defaultRange?.end),
      })
    } else {
      update({ "untouchedStart": false, "startsOnSubmission": String(startsOnSubmission) });
    };
  };

  const minStartsDate = getMaxMinOptions(minDate);
  const maxStartsDate = ends ? ends : getMaxMinOptions(maxDate);

  const minEndsDate = startsOnSubmission ? new Date().toISOString() : starts ? starts : minStartsDate
  const maxEndsDate = getMaxMinOptions(maxDate)


  const renderPicker = (key: "start" | "end", children: any) => {
    const propSchema: any = (schema.properties || {})[key];
    const label = t(propSchema?.title);

    const errors = errorSchema[key]?.__errors || []
    return (
      <Form.Field error={errors.length} required={schema.required?.includes(key)}>
        <Header className="Field-label" as="label"> {label} </Header>
        {children}
        <ErrorList
          rawErrors={errors}
        />
      </Form.Field>
    )
  };


  const defaultEndValue = startsOnSubmission === "Y" ? roundToNearestHour(roundToNearestHour(new Date(), startsOptions?.hourRoundingValue || hourRoundingValue), endsOptions?.hourRoundingValue || hourRoundingValue) : starts ? new Date(starts) : undefined

  return (
    <Fragment>
      <Fragment>
        {renderPicker("start", (
          <Fragment>
            <Form.Group>
              <Form.Field>
                <Radio
                  label="Myöntöhetki"
                  value={"Y"}
                  checked={startsOnSubmission === "Y"}
                  onChange={updateStartsOnSubmission}
                />
              </Form.Field>
              <Form.Field>
                <Radio
                  label="Valitse aika"
                  value={"N"}
                  checked={startsOnSubmission === "N"}
                  onChange={updateStartsOnSubmission}
                />
              </Form.Field>
            </Form.Group>

            {(startsOnSubmission === "N") || (startsOnSubmission === "Y" && untouchedStart) ? (
              <DateTimePicker
                key={[ends, starts].join("")}
                value={starts}
                onChange={updateStarts}
                minDate={minStartsDate}
                maxDate={maxStartsDate}
                defaultTime={startsOptions?.defaultTime}
                hourRoundingValue={startsOptions?.hourRoundingValue || hourRoundingValue}
                format={schemaFormat}
                pastDateWarning={!untouchedStart && pastDateWarning}
                disabled={startsOnSubmission === "Y"}
              />) : null}

          </Fragment>
        ))}
      </Fragment>

      {renderPicker("end", (
        <DateTimePicker
          key={String(starts) + String(startsOnSubmission)}
          value={ends}
          onChange={updateEnds}
          minDate={minEndsDate}
          maxDate={maxEndsDate}
          defaultValue={defaultEndValue}
          defaultValueBase={defaultEndValue}
          hourRoundingValue={endsOptions?.hourRoundingValue || hourRoundingValue}

          format={schemaFormat}
        />
      ))}

    </Fragment>
  );
}

DateRangeWidget.defaultProps = {
  required: false,
  disabled: false,
};

export default DateRangeWidget;
